/**
 * Seeded Random Number Generator
 * LCG (Linear Congruential Generator) for deterministic settlement generation.
 * Same seed always produces the same sequence.
 */

export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0; // ensure unsigned 32-bit
  }

  /** Advance state and return next value in [0, 1) */
  next(): number {
    // Numerical Recipes LCG constants
    this.state = (this.state * 1664525 + 1013904223) >>> 0;
    return this.state / 0x100000000;
  }

  /** Random float in [min, max) */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Random integer in [min, max] (inclusive) */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** Random boolean with given probability (default 0.5) */
  bool(probability = 0.5): boolean {
    return this.next() < probability;
  }

  /** Pick a random element from an array */
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }

  /** Shuffle an array in place (Fisher-Yates) */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /** Pick n unique random elements from an array */
  sample<T>(arr: readonly T[], n: number): T[] {
    const copy = [...arr];
    this.shuffle(copy);
    return copy.slice(0, Math.min(n, arr.length));
  }

  /** Random angle in [0, 2π) */
  angle(): number {
    return this.next() * Math.PI * 2;
  }

  /** Gaussian-ish distribution using Box-Muller (pair discards one) */
  gaussian(mean = 0, stddev = 1): number {
    const u1 = this.next() || 0.0001; // avoid log(0)
    const u2 = this.next();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return mean + z * stddev;
  }
}
